// v1.8 P0: 6 Incoterm 提示卡 — 订单详情页底部说明
// 数据: apps/web/src/lib/incoterms-info.ts (INCOTERM_INFO)
// 设计: icon (alert-triangle / check-circle-2 / lightbulb / circle-alert) 区分 + 严重性颜色
import { getIncotermInfo } from '../lib/incoterms-info';
import { Icon } from './Icon';

interface IncotermCardProps {
  incoterm: string;
  place?: string;
}

// icon 字段 (emoji 或 kebab-case) → lucide class 映射
// ⚠️/✅/💡/🔴 在 EMOJI_TO_ICON 自动转, 这里只指定背景色
const ICON_BG: Record<string, string> = {
  '⚠️': 'bg-warn/15 text-warn',
  '✅': 'bg-good/15 text-good',
  '💡': 'bg-info/15 text-info',
  '🔴': 'bg-bad/15 text-bad',
  'alert-triangle': 'bg-warn/15 text-warn',
  'check-circle-2': 'bg-good/15 text-good',
  lightbulb: 'bg-info/15 text-info',
  'circle-alert': 'bg-bad/15 text-bad',
};

export function IncotermCard({ incoterm, place }: IncotermCardProps) {
  const info = getIncotermInfo(incoterm);
  if (!info) return null;

  return (
    <div className="bg-surface border border-line rounded p-5 mb-4">
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <h2 className="text-sm font-semibold flex items-center gap-1.5">
          <Icon name="package" size="sm" className="text-accent" />
          Incoterm 提示
        </h2>
        <span className="text-xs text-ink-3">
          {info.code} {place ? `· ${place}` : ''} · {info.fullNameZh}
        </span>
      </div>

      {/* 责任 + 风险分界 — 两列 (移动端单列) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3 text-xs">
        <div className="bg-surface-2 rounded p-2.5">
          <div className="text-ink-3 mb-0.5">卖方责任</div>
          <div className="text-ink-1">{info.sellerZh}</div>
        </div>
        <div className="bg-surface-2 rounded p-2.5">
          <div className="text-ink-3 mb-0.5">买方责任</div>
          <div className="text-ink-1">{info.buyerZh}</div>
        </div>
        <div className="bg-surface-2 rounded p-2.5">
          <div className="text-ink-3 mb-0.5">风险分界</div>
          <div className="text-ink-1">{info.riskTransferZh}</div>
        </div>
        <div className="bg-surface-2 rounded p-2.5">
          <div className="text-ink-3 mb-0.5">适合场景</div>
          <div className="text-ink-1">{info.scenarioZh}</div>
        </div>
      </div>

      {/* 注意项 (icon 区分) */}
      <div>
        <div className="text-xs text-ink-3 mb-1.5">注意项</div>
        <ul className="space-y-1.5">
          {info.notes.map((note, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static small list, index is stable
            <li key={i} className="flex items-start gap-2 text-xs">
              <span
                className={`shrink-0 w-5 h-5 rounded flex items-center justify-center ${ICON_BG[note.icon] ?? 'bg-surface-2'}`}
              >
                <Icon name={note.icon} size="xs" strokeWidth={2} />
              </span>
              <span className="flex-1 text-ink-1 leading-relaxed">{note.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
